import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Button,
  Grid,
  Box,
  Autocomplete,
  Skeleton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { useAppState } from "../../../appContext";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { LoadingButton } from "@mui/lab";
import { useLocation, useNavigate } from "react-router";
import {
  useAddVenue,
  useFetchVenueById,
  useFetchVenueByInternalLink,
  useUpdateVenue,
  useFetchVenuesSummary,
} from "../../../hooks/venueHooks";
import ImageCropper from "../Events/ImageCropper";
import SocialMediaLinkAdder from "../../SocialMediaLinkAdder";
import { GeoPoint } from "firebase/firestore";
import PaginatedAutocomplete from "../../Elements/PaginatedAutocomplete";
import { generateCleanWordsWithLimit } from "../../../utils/utility";
import { auth } from "../../../firebase-config";
import mapboxgl from "mapbox-gl";

// ---------------------------------------------------------------------------
// Mapbox token
// ---------------------------------------------------------------------------
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Expand common street abbreviations so Mapbox finds them reliably
function expandAbbrevs(s) {
  if (!s) return s;
  return s
    .replace(/\bHwy\b/gi, "Highway")
    .replace(/\bSt\b/g, "Street")
    .replace(/\bAve?\b/gi, "Avenue")
    .replace(/\bBlvd\b/gi, "Boulevard")
    .replace(/\bRd\b/g, "Road")
    .replace(/\bDr\b/g, "Drive")
    .replace(/\bLn\b/g, "Lane")
    .replace(/\bCt\b/g, "Court")
    .replace(/\bPl\b/g, "Place")
    .replace(/\bPkwy\b/gi, "Parkway");
}

// ---------------------------------------------------------------------------
// Geocoding REST call — accepts either (address, city, state, zip) or a single
// pre-built query string when city/state/zip are omitted.
// Automatically retries with expanded abbreviations if first attempt returns 0 results.
// ---------------------------------------------------------------------------
async function geocodeAddress(address, city, state, zip) {
  async function fetchFeatures(query) {
    if (!query) return [];
    if (!MAPBOX_TOKEN) {
      console.error("Geocode error: REACT_APP_MAPBOX_ACCESS_TOKEN is not configured");
      throw new Error("Mapbox token is not configured. Add REACT_APP_MAPBOX_ACCESS_TOKEN to your environment variables.");
    }
    const url =
      `https://api.mapbox.com/geocoding/v5/mapbox.places/` +
      `${encodeURIComponent(query)}.json` +
      `?types=address&limit=5&country=US&access_token=${MAPBOX_TOKEN}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Mapbox geocoding error: ${res.status}`);
    const data = await res.json();
    return data.features || [];
  }

  // If called with a single raw string (city/state/zip all falsy), use it directly
  const query =
    !city && !state && !zip
      ? address.trim()
      : [address, city, state, zip].filter(Boolean).join(", ");
  if (!query) return [];

  // First attempt with the query as-is
  let features = await fetchFeatures(query);

  // If no results, retry with street abbreviations expanded
  if (features.length === 0) {
    const expanded = expandAbbrevs(query);
    if (expanded !== query) {
      features = await fetchFeatures(expanded);
    }
  }

  return features;
}

// Full state name → 2-letter abbreviation
const STATE_ABBREVS = {
  "alabama":"AL","alaska":"AK","arizona":"AZ","arkansas":"AR","california":"CA",
  "colorado":"CO","connecticut":"CT","delaware":"DE","florida":"FL","georgia":"GA",
  "hawaii":"HI","idaho":"ID","illinois":"IL","indiana":"IN","iowa":"IA",
  "kansas":"KS","kentucky":"KY","louisiana":"LA","maine":"ME","maryland":"MD",
  "massachusetts":"MA","michigan":"MI","minnesota":"MN","mississippi":"MS",
  "missouri":"MO","montana":"MT","nebraska":"NE","nevada":"NV","new hampshire":"NH",
  "new jersey":"NJ","new mexico":"NM","new york":"NY","north carolina":"NC",
  "north dakota":"ND","ohio":"OH","oklahoma":"OK","oregon":"OR","pennsylvania":"PA",
  "rhode island":"RI","south carolina":"SC","south dakota":"SD","tennessee":"TN",
  "texas":"TX","utah":"UT","vermont":"VT","virginia":"VA","washington":"WA",
  "west virginia":"WV","wisconsin":"WI","wyoming":"WY","district of columbia":"DC",
};

function toStateAbbrev(name) {
  if (!name) return "";
  // Already a 2-letter code
  if (name.length === 2) return name.toUpperCase();
  return STATE_ABBREVS[name.toLowerCase()] || name;
}

// Parse one Mapbox feature into flat address fields
function parseFeature(feature) {
  const coords = feature.geometry?.coordinates; // [lng, lat]
  const ctx = {};
  (feature.context || []).forEach((c) => {
    // prefer short_code for region (e.g. "US-MD") over full name
    const type = c.id.split(".")[0];
    ctx[type] = c.text;
    if (type === "region" && c.short_code) {
      // short_code is like "US-MD" — strip the country prefix
      ctx["region_code"] = c.short_code.replace(/^[A-Z]{2}-/, "");
    }
  });
  const street = [feature.address, feature.text].filter(Boolean).join(" ");
  const rawState = ctx["region"] || "";
  const stateAbbrev = ctx["region_code"] || toStateAbbrev(rawState);
  return {
    address: street || feature.place_name?.split(",")[0] || "",
    city: ctx["place"] || "",
    state: stateAbbrev,
    zip: ctx["postcode"] || "",
    lat: coords ? String(coords[1]) : "",
    lon: coords ? String(coords[0]) : "",
    placeName: feature.place_name || "",
  };
}

// ---------------------------------------------------------------------------
// Mini Mapbox map — raw mapbox-gl, draggable marker
// ---------------------------------------------------------------------------
const VenueMap = ({ lat, lon, onDrag }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const initializedRef = useRef(false);

  const parsedLat = parseFloat(lat);
  const parsedLon = parseFloat(lon);
  const valid = !isNaN(parsedLat) && !isNaN(parsedLon);

  // Mount / unmount the map
  useEffect(() => {
    if (!valid || !containerRef.current || initializedRef.current) return;
    initializedRef.current = true;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [parsedLon, parsedLat],
      zoom: 15,
    });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    mapRef.current = map;

    const marker = new mapboxgl.Marker({ draggable: true, color: "#307fc1" })
      .setLngLat([parsedLon, parsedLat])
      .addTo(map);
    markerRef.current = marker;

    marker.on("dragend", () => {
      const ll = marker.getLngLat();
      onDrag(String(ll.lat), String(ll.lng));
    });

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      initializedRef.current = false;
    };
    // intentionally only runs when valid first becomes true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid]);

  // Keep the marker/center in sync when coordinates change from outside
  useEffect(() => {
    if (!valid || !markerRef.current || !mapRef.current) return;
    markerRef.current.setLngLat([parsedLon, parsedLat]);
    mapRef.current.easeTo({ center: [parsedLon, parsedLat] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon]);

  if (!valid) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: 260,
        borderRadius: 1,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        mt: 1,
        mb: 2,
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Main form
// ---------------------------------------------------------------------------
const VenueForm = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const venueId = searchParams.get("venueId");
  const [state] = useAppState();

  const { venue: venueData, loading: venueDataLoading } = useFetchVenueById(venueId);
  const { venues: venueSummaries } = useFetchVenuesSummary(null, true);
  const [isEditMode, setIsEditMode] = useState(false);

  // Form fields
  const [guid, setGuid] = useState(uuidv4());
  const [venueName, setVenueName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [venueUrl, setVenueUrl] = useState("");
  const [zone, setZone] = useState("America/New_York");
  const [venueState, setVenueState] = useState("");
  const [socialMediaLinks, setSocialMediaLinks] = useState([]);
  const [longitude, setLongitude] = useState("");
  const [latitude, setLatitude] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [attrBeach, setAttrBeach] = useState(false);
  const [attrWaterfront, setAttrWaterfront] = useState(false);
  const [attrOutdoorPatio, setAttrOutdoorPatio] = useState(false);
  const [attrOutdoorEvents, setAttrOutdoorEvents] = useState(false);
  const [attrDinnerEntertainment, setAttrDinnerEntertainment] = useState(false);
  const [attrVineyard, setAttrVineyard] = useState(false);
  const [image, setImage] = useState(null);
  const [internalLink, setInternalLink] = useState("");

  // Geocoding UI state
  // null | 'loading' | 'verified' | 'multiple' | 'failed'
  const [geocodeStatus, setGeocodeStatus] = useState(null);
  const [geocodeCandidates, setGeocodeCandidates] = useState([]);
  const [showPickDialog, setShowPickDialog] = useState(false);

  // Duplicate-address warning
  const [duplicateVenues, setDuplicateVenues] = useState([]);
  const [duplicateDismissed, setDuplicateDismissed] = useState(false);

  // Track the last address combo we actually geocoded to avoid re-running
  const lastGeocodedRef = useRef("");

  const { addVenue, loading: addingVenue } = useAddVenue();
  const { updateVenue, loading: updatingVenue } = useUpdateVenue();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { fetchVenue } = useFetchVenueByInternalLink();

  const [errors, setErrors] = useState({
    venueName: "",
    city: "",
    zone: "",
    address: "",
    latitude: "",
    longitude: "",
    zip: "",
    venueState: "",
    internalLink: "",
  });

  // -------------------------------------------------------------------------
  // Duplicate check — pure function, no closure issues
  // Normalizes both sides: lowercase, collapse whitespace, expand common abbrevs
  // -------------------------------------------------------------------------
  const normalizeAddr = (s) => {
    if (!s) return "";
    // Normalize both abbreviations and full forms so they compare equal
    const abbrevMap = {
      "highway": "hwy", "street": "st", "avenue": "ave", "boulevard": "blvd",
      "road": "rd", "drive": "dr", "lane": "ln", "court": "ct",
      "place": "pl", "parkway": "pkwy",
    };
    let normalized = s.toLowerCase().trim().replace(/\s+/g, " ").replace(/[.,]+$/, "");
    // Collapse full words → abbreviations so both sides end up the same short form
    Object.entries(abbrevMap).forEach(([full, abbrev]) => {
      normalized = normalized.replace(new RegExp(`\\b${full}\\b`, "g"), abbrev);
    });
    return normalized;
  };

  // Extract the leading house/street number from an address string
  const extractNumber = (s) => {
    const m = s && s.match(/^\d+/);
    return m ? m[0] : null;
  };

  const checkDuplicates = (rawAddress, summaries, editMode, currentId, rawCity, rawZip) => {
    if (!rawAddress || !summaries?.length) return;
    const needle = normalizeAddr(rawAddress);
    const needleNum = extractNumber(rawAddress);
    const needleCity = (rawCity || "").toLowerCase().trim();
    const needleZip = (rawZip || "").trim();

    const matches = summaries.filter((v) => {
      if (editMode && v.id === currentId) return false;
      // 1. Exact normalized address match
      if (normalizeAddr(v.address1) === needle) return true;
      // 2. Same house number + same city/zip (catches "Hwy" vs "Highway" vs "Governor Ritchie Highway")
      if (needleNum && extractNumber(v.address1) === needleNum) {
        const vCity = (v.venue_city || "").toLowerCase().trim();
        const vZip = (v.VENUE_ZIP || "").trim();
        if ((needleCity && vCity === needleCity) || (needleZip && vZip === needleZip)) {
          return true;
        }
      }
      return false;
    });
    setDuplicateVenues(matches);
    setDuplicateDismissed(false);
  };

  // Keep a ref to the last geocoded address so we can re-run duplicate check
  // if venueSummaries loads after geocoding completes
  const lastGeocodedAddressRef = useRef("");

  // Re-run duplicate check whenever venueSummaries loads (handles race condition)
  useEffect(() => {
    if (lastGeocodedAddressRef.current && venueSummaries?.length) {
      checkDuplicates(lastGeocodedAddressRef.current, venueSummaries, isEditMode, venueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueSummaries]);

  // -------------------------------------------------------------------------
  // Apply geocoded result to all fields
  // -------------------------------------------------------------------------
  const applyResult = (parsed) => {
    if (parsed.address) setAddress(parsed.address);
    if (parsed.city) setCity(parsed.city);
    if (parsed.state) setVenueState(parsed.state);
    if (parsed.zip) setZip(parsed.zip);
    if (parsed.lat) setLatitude(parsed.lat);
    if (parsed.lon) setLongitude(parsed.lon);
    lastGeocodedAddressRef.current = parsed.address || "";
    // Pass city and zip so duplicate check can match on house number + city/zip
    checkDuplicates(parsed.address, venueSummaries, isEditMode, venueId, parsed.city, parsed.zip);
  };

  // -------------------------------------------------------------------------
  // Core geocode runner
  // Pass (rawString) with no other args for single-string mode (paste shortcut)
  // Pass (addr, city, state, zip) for structured mode
  // -------------------------------------------------------------------------
  const runGeocode = async (addr, cty, st, zp) => {
    const isRawMode = !cty && !st && !zp;
    if (!addr) return;
    if (!isRawMode && (!cty || !st || !zp)) return;

    const combo = isRawMode ? addr : `${addr}||${cty}||${st}||${zp}`;
    if (lastGeocodedRef.current === combo) return;

    lastGeocodedRef.current = combo;
    setGeocodeStatus("loading");

    try {
      // In raw mode pass only addr; geocodeAddress will use it as the full query
      const features = isRawMode
        ? await geocodeAddress(addr)
        : await geocodeAddress(addr, cty, st, zp);
      if (!features || features.length === 0) {
        setGeocodeStatus("failed");
        lastGeocodedRef.current = ""; // allow retry
        return;
      }
      if (features.length === 1) {
        applyResult(parseFeature(features[0]));
        setGeocodeStatus("verified");
      } else {
        setGeocodeCandidates(features);
        setShowPickDialog(true);
        setGeocodeStatus("multiple");
      }
    } catch (err) {
      console.error("Geocode error:", err);
      setGeocodeStatus("failed");
      lastGeocodedRef.current = ""; // allow retry
    }
  };

  // -------------------------------------------------------------------------
  // Debounced auto-geocode: fires 500ms after the user stops typing
  // -------------------------------------------------------------------------
  const debounceRef = useRef(null);

  const scheduleGeocode = (addr, cty, st, zp) => {
    clearTimeout(debounceRef.current);
    if (!addr || !cty || !st || !zp) return;
    debounceRef.current = setTimeout(() => {
      runGeocode(addr, cty, st, zp);
    }, 500);
  };

  // Refs that always hold the latest field values (no stale closure issue)
  const addressRef = useRef(address);
  const cityRef = useRef(city);
  const venueStateRef = useRef(venueState);
  const zipRef = useRef(zip);

  // Keep refs in sync with state
  useEffect(() => { addressRef.current = address; }, [address]);
  useEffect(() => { cityRef.current = city; }, [city]);
  useEffect(() => { venueStateRef.current = venueState; }, [venueState]);
  useEffect(() => { zipRef.current = zip; }, [zip]);

  // Helper: wrap a field setter so it also schedules geocode and resets status
  const makeHandler = (setter, fieldRef) => (value) => {
    setter(value);
    fieldRef.current = value; // update ref immediately so scheduleGeocode sees the new value
    // Reset verified status when user edits address fields
    setGeocodeStatus(null);
    lastGeocodedRef.current = "";
    scheduleGeocode(addressRef.current, cityRef.current, venueStateRef.current, zipRef.current);
  };

  // -------------------------------------------------------------------------
  // Load venue in edit mode
  // -------------------------------------------------------------------------
  const removeSlashes = (str) => {
    if (!str) return "";
    if (str.startsWith("/")) str = str.slice(1);
    if (str.endsWith("/")) str = str.slice(0, -1);
    return str;
  };

  useEffect(() => {
    if (venueData) {
      setIsEditMode(true);
      setGuid(venueData.id);
      setVenueName(venueData.venue_name);
      setAddress(venueData.address1);
      setCity(venueData.venue_city);
      setZip(venueData.VENUE_ZIP);
      setVenueUrl(venueData.venue_url);
      setZone(venueData.tzone);
      setVenueState(venueData.venue_state);
      setSocialMediaLinks(venueData.socialMediaLinks || []);
      setLongitude(venueData.lon);
      setLatitude(venueData.lat);
      setDescription(venueData.description || "");
      setImageUrl(venueData.imageUrl || "");
      setAttrBeach(venueData.attr_beach || false);
      setAttrWaterfront(venueData.attr_waterfront || false);
      setAttrOutdoorPatio(venueData.attr_outdoor_patio || false);
      setAttrOutdoorEvents(venueData.attr_outdoor_events || false);
      setAttrDinnerEntertainment(venueData.attr_dinner_entertainment || false);
      setAttrVineyard(venueData.attr_vineyard || false);
      setInternalLink(removeSlashes(venueData.internallink) || "");
      // Mark as verified so we don't auto-geocode on load
      setGeocodeStatus("verified");
      lastGeocodedRef.current = `${venueData.address1}||${venueData.venue_city}||${venueData.venue_state}||${venueData.VENUE_ZIP}`;
    }
  }, [venueData]);

  // -------------------------------------------------------------------------
  // Validation & submit (unchanged)
  // -------------------------------------------------------------------------
  const validate = () => {
    let temp = {};
    temp.venueName = venueName ? "" : "This field is required.";
    temp.city = city ? "" : "This field is required.";
    temp.address = address ? "" : "This field is required.";
    temp.latitude = latitude ? "" : "This field is required.";
    temp.longitude = longitude ? "" : "This field is required.";
    temp.zip = zip ? "" : "This field is required.";
    temp.venueState = venueState ? "" : "This field is required.";
    temp.internalLink = internalLink
      ? /^[a-zA-Z0-9-]+$/.test(internalLink)
        ? ""
        : "Only alphanumeric characters and hyphens are allowed."
      : "This field is required.";
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleImageCrop = (img, croppedDataUrl) => {
    if (!img || !croppedDataUrl) { setImage(""); return; }
    setImage(img);
    setImageUrl(croppedDataUrl);
  };

  const handleImageUpload = async (imageFile) => {
    if (!imageFile) return imageUrl || "";
    const storage = getStorage();
    const storageRef = ref(storage, "venues/" + venueName.replaceAll(" ", "-"));
    try {
      const snapshot = await uploadBytes(storageRef, imageFile);
      return await getDownloadURL(snapshot.ref);
    } catch (error) {
      console.error("Error uploading image: ", error);
      return "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validate()) {
      setIsLoading(true);
      const finalImageUrl = image ? await handleImageUpload(image) : imageUrl;
      const userId = auth.currentUser?.uid || null;

      const formData = {
        id: guid,
        venue_name: venueName,
        venue_city: city,
        venue_url: venueUrl,
        description,
        lon: longitude,
        lat: latitude,
        modifieddate: dayjs().toISOString(),
        imageUrl: finalImageUrl,
        tzone: zone,
        VENUE_ZIP: zip,
        address1: address,
        venue_state: venueState,
        socialMediaLinks,
        attr_beach: attrBeach,
        attr_waterfront: attrWaterfront,
        attr_outdoor_patio: attrOutdoorPatio,
        attr_outdoor_events: attrOutdoorEvents,
        attr_dinner_entertainment: attrDinnerEntertainment,
        attr_vineyard: attrVineyard,
        coordinates: new GeoPoint(parseFloat(latitude), parseFloat(longitude)),
        searchTerms: generateCleanWordsWithLimit(venueName.toLowerCase()),
        title_lowercase: venueName.toLowerCase(),
        internallink: `/${internalLink.toLowerCase()}/`,
        userId,
      };

      fetchVenue(internalLink).then(async (res) => {
        if (isEditMode) {
          if (res?.id && res?.id !== venueData.id) {
            setErrors((e) => ({ ...e, internalLink: `${internalLink} already exists` }));
          } else {
            await updateVenue(guid, formData);
            navigateToDetails(guid);
          }
        } else {
          if (res?.id) {
            setErrors((e) => ({ ...e, internalLink: `${internalLink} already exists` }));
          } else {
            await addVenue({ ...formData, createdAt: dayjs().toISOString() });
            navigateToDetails(guid);
          }
        }
      });
      setIsLoading(false);
    }
  };

  const navigateToDetails = (id) => {
    if (id) navigate(`/venues?venueId=${id}`, { state: { fromNavigateToDetails: true } });
    else navigate("/venues");
  };

  // -------------------------------------------------------------------------
  // Skeleton while loading edit-mode data
  // -------------------------------------------------------------------------
  if (venueDataLoading) {
    return (
      <Box width="100%">
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
        <Skeleton height={100} animation="wave" />
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Container maxWidth={false} sx={{ mt: 12 }}>
      <Card raised sx={{ margin: "auto", maxWidth: "calc(100% - 32px)", boxShadow: 3 }}>
        <Box sx={{ bgcolor: "#307fc1", color: "white", p: 2 }}>
          <Typography variant="h6">{isEditMode ? "Edit Venue" : "Add Venue"}</Typography>
        </Box>
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
              <ImageCropper
                src={isEditMode ? imageUrl : ""}
                onCrop={handleImageCrop}
                aspectRatio={1}
              />

              <br /><br />

              <TextField
                fullWidth
                label="Venue Name"
                value={venueName}
                onChange={(e) => {
                  const name = e.target.value;
                  setVenueName(name);
                  // Only auto-fill slug in add mode and only if the user hasn't manually edited it
                  if (!isEditMode) {
                    const slug = name
                      .toLowerCase()
                      .trim()
                      .replace(/[^a-z0-9\s-]/g, "")   // remove non-alphanumeric except spaces/hyphens
                      .replace(/\s+/g, "-")             // spaces → hyphens
                      .replace(/-+/g, "-")              // collapse multiple hyphens
                      .replace(/^-|-$/g, "");           // trim leading/trailing hyphens
                    setInternalLink(slug);
                  }
                }}
                error={!!errors.venueName}
                helperText={errors.venueName || ""}
                sx={{ my: 2 }}
                required
              />

              {/* ── Quick full-address paste field ───────────────────── */}
              <Box sx={{ mb: 1 }}>
                <TextField
                  fullWidth
                  label='Paste full address here (e.g. "489 Ritchie Hwy, Severna Park, MD 21146")'
                  placeholder="Paste and press Lookup Address — fields will be filled automatically"
                  onPaste={(e) => {
                    // grab pasted text immediately (before React state update)
                    const pasted = e.clipboardData.getData("text").trim();
                    if (!pasted) return;
                    // clear the last geocoded ref so the lookup re-runs
                    lastGeocodedRef.current = "";
                    setGeocodeStatus(null);
                    // schedule geocode using the raw pasted string directly
                    clearTimeout(debounceRef.current);
                    debounceRef.current = setTimeout(() => {
                      runGeocode(pasted); // raw string mode — no city/state/zip needed
                    }, 300);
                  }}
                  // keep as uncontrolled / display-only after paste
                  inputProps={{ style: { color: "#555" } }}
                  sx={{ mb: 1 }}
                  size="small"
                  helperText="Optional shortcut — paste a full address to auto-fill all fields below"
                />
              </Box>

              {/* ── Address fields ─────────────────────────────────────── */}
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={address}
                    onChange={(e) => makeHandler(setAddress, addressRef)(e.target.value)}
                    error={!!errors.address}
                    helperText={errors.address || ""}
                    sx={{ my: 2 }}
                    required
                  />
                </Grid>
                <Grid sx={{ my: 2 }} item xs={12} sm={2}>
                  <PaginatedAutocomplete
                    options={state.cities || []}
                    label="City"
                    value={city}
                    onChange={(event, newValue) =>
                      makeHandler(setCity, cityRef)(newValue || "")
                    }
                    error={!!errors.city}
                    helperText={errors.city || ""}
                    freeSolo={true}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Autocomplete
                    fullWidth
                    options={state.states || []}
                    getOptionLabel={(option) => option}
                    value={venueState}
                    onChange={(event, newValue) =>
                      makeHandler(setVenueState, venueStateRef)(newValue || "")
                    }
                    freeSolo={true}
                    isOptionEqualToValue={(option, value) => !value || option === value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        error={!!errors.venueState}
                        helperText={errors.venueState || ""}
                        sx={{ my: 2 }}
                        required
                        autoComplete="off"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={2} sx={{ display: "none" }}>
                  <TextField fullWidth label="Zone" disabled value={zone} sx={{ my: 2 }} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Zip Code"
                    value={zip}
                    onChange={(e) => makeHandler(setZip, zipRef)(e.target.value)}
                    error={!!errors.zip}
                    helperText={errors.zip || ""}
                    sx={{ my: 2 }}
                    required
                  />
                </Grid>
              </Grid>

              {/* ── Geocode status row ─────────────────────────────────── */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, minHeight: 40, flexWrap: "wrap" }}>
                {/* Always-visible Lookup button */}
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={geocodeStatus === "loading" ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  disabled={geocodeStatus === "loading" || !address}
                  onClick={() => {
                    lastGeocodedRef.current = "";
                    // If city/state/zip are filled use structured mode; otherwise raw mode
                    const a = addressRef.current;
                    const c = cityRef.current;
                    const s = venueStateRef.current;
                    const z = zipRef.current;
                    if (c && s && z) {
                      runGeocode(a, c, s, z);
                    } else {
                      // raw mode: send whatever is in address (may include full address)
                      runGeocode(a);
                    }
                  }}
                >
                  Lookup Address
                </Button>

                {/* Status feedback */}
                {geocodeStatus === "loading" && (
                  <Typography variant="body2" color="text.secondary">
                    Looking up address…
                  </Typography>
                )}
                {geocodeStatus === "verified" && (
                  <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label="✓ Address Verified"
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                )}
                {geocodeStatus === "failed" && (
                  <Typography variant="body2" color="error">
                    Address not found. Check the address fields and try again.
                  </Typography>
                )}
              </Box>

              {/* ── Duplicate address warning ─────────────────────────── */}
              {duplicateVenues.length > 0 && !duplicateDismissed && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => setDuplicateDismissed(true)}>
                      Dismiss
                    </Button>
                  }
                >
                  <strong>This address already exists</strong> for:{" "}
                  {duplicateVenues.map((v, i) => (
                    <React.Fragment key={v.id}>
                      <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        sx={{ textDecoration: "underline", p: 0, minWidth: 0, fontWeight: 700 }}
                        onClick={() => navigate(`/venues?venueId=${v.id}`)}
                      >
                        {v.venue_name}
                      </Button>
                      {i < duplicateVenues.length - 1 && ", "}
                    </React.Fragment>
                  ))}
                  . This venue may be a different room in the same building.
                </Alert>
              )}

              {/* ── Mini map ──────────────────────────────────────────── */}
              <VenueMap
                lat={latitude}
                lon={longitude}
                onDrag={(newLat, newLon) => {
                  setLatitude(newLat);
                  setLongitude(newLon);
                }}
              />

              {/* ── Coordinates & other fields ───────────────────────── */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Latitude (40.728...)"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    error={!!errors.latitude}
                    helperText={errors.latitude || ""}
                    sx={{ my: 2 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Longitude (-73.999...)"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    error={!!errors.longitude}
                    helperText={errors.longitude || ""}
                    sx={{ my: 2 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Venue URL"
                    value={venueUrl}
                    onChange={(e) => setVenueUrl(e.target.value)}
                    sx={{ my: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="venue directory name"
                    value={internalLink}
                    onChange={(e) => setInternalLink(e.target.value)}
                    error={!!errors.internalLink}
                    helperText={errors.internalLink || ""}
                    required
                    sx={{ my: 2 }}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12} sm={12} sx={{ pb: 1 }}>
                <Typography variant="body1" sx={{ pt: 1 }}>
                  Tip: coordinates are filled automatically as you type the address. You can also
                  drag the map marker to fine-tune the coordinates.
                  <br />
                  Venue Directory name will be the URL. If you type <strong>bitterend</strong>,
                  the URL will be https://www.webtunes.com/venues/<strong>bitterend</strong>
                </Typography>
              </Grid>

              <SocialMediaLinkAdder
                initialLinks={socialMediaLinks}
                onLinksChange={(v) => setSocialMediaLinks(v)}
              />

              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={4}
                sx={{ my: 2 }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                  Venue Attributes
                </Typography>
                <FormGroup row sx={{ gap: 1 }}>
                  <FormControlLabel
                    control={<Checkbox checked={attrBeach} onChange={(e) => setAttrBeach(e.target.checked)} />}
                    label="🏖️ Beach"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={attrWaterfront} onChange={(e) => setAttrWaterfront(e.target.checked)} />}
                    label="🌊 Waterfront"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={attrOutdoorPatio} onChange={(e) => setAttrOutdoorPatio(e.target.checked)} />}
                    label="☀️ Outdoor Patio"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={attrOutdoorEvents} onChange={(e) => setAttrOutdoorEvents(e.target.checked)} />}
                    label="🌿 Outdoor Events"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={attrDinnerEntertainment} onChange={(e) => setAttrDinnerEntertainment(e.target.checked)} />}
                    label="🍽️ Dinner Entertainment"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={attrVineyard} onChange={(e) => setAttrVineyard(e.target.checked)} />}
                    label="🍷 Vineyard"
                  />
                </FormGroup>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                <Button variant="outlined" color="primary" onClick={() => navigateToDetails(null)}>
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={addingVenue || updatingVenue || isLoading}
                >
                  Submit
                </LoadingButton>
              </Box>
            </form>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* ── Multi-result pick dialog ────────────────────────────────── */}
      <Dialog open={showPickDialog} onClose={() => setShowPickDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Multiple addresses found — please select one</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List disablePadding>
            {geocodeCandidates.map((feature, i) => (
              <ListItem key={i} disablePadding divider>
                <ListItemButton
                  onClick={() => {
                    const parsed = parseFeature(feature);
                    applyResult(parsed);
                    setGeocodeStatus("verified");
                    setShowPickDialog(false);
                    setGeocodeCandidates([]);
                  }}
                >
                  <ListItemText primary={feature.place_name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowPickDialog(false);
              setGeocodeStatus("failed");
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VenueForm;
